import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import PhoneInput from '@/components/PhoneInput';
import { toast } from 'sonner';
import { Users, Search, Mail, Phone, MapPin, Calendar, UserCircle, Eye, Edit, X, History, Activity, TrendingUp, Plus, Download, ChevronLeft, ChevronRight, StickyNote, Loader2, Trash2, RefreshCw } from 'lucide-react';
import { formatPhoneDisplay, parsePhoneNumber } from '@/utils/phoneUtils';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Leads = () => {
  const { token, agent } = useAuth();
  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [leadPage, setLeadPage] = useState(1);
  const [leadPageSize, setLeadPageSize] = useState(20);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [addLeadModalOpen, setAddLeadModalOpen] = useState(false);
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [notesLead, setNotesLead] = useState(null);
  const [leadNotes, setLeadNotes] = useState([]);
  const [newNoteText, setNewNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [visitHistory, setVisitHistory] = useState([]);
  const [activityLog, setActivityLog] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    phone: '',
    working_with_agent: '',
    agent_name: '',
    buying_timeline: '',
    budget_range: '',
    preferred_locations: '',
    interested_in_property: '',
    request_private_showing: false,
    notes: '',
    interest_level: '',
    tag: ''
  });
  const [addLeadForm, setAddLeadForm] = useState({
    name: '',
    email: '',
    phone: '',
    working_with_agent: '',
    agent_name: '',
    buying_timeline: '',
    budget_range: '',
    preferred_locations: '',
    interested_in_property: '',
    property_id: '',
    property_address_manual: '',
    contact_date: new Date().toISOString().split('T')[0],
    notes: '',
    source: 'manual'
  });
  const [agentProperties, setAgentProperties] = useState([]);
  const [openHouses, setOpenHouses] = useState([]);
  const [saving, setSaving] = useState(false);
  
  const isBroker = agent?.role === 'broker';

  useEffect(() => {
    fetchLeads();
    fetchOpenHouses();
  }, []);

  const fetchOpenHouses = async () => {
    try {
      const response = await axios.get(`${API}/events`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOpenHouses(response.data);
    } catch (error) {
      console.error('Failed to fetch open houses:', error);
    }
  };

  useEffect(() => {
    if (searchTerm) {
      const filtered = leads.filter(lead => 
        lead.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        lead.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        lead.property_address?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredLeads(filtered);
    } else {
      setFilteredLeads(leads);
    }
  }, [searchTerm, leads]);

  useEffect(() => {
    setLeadPage(1);
  }, [searchTerm]);

  const fetchLeads = async () => {
    try {
      const response = await axios.get(`${API}/leads`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Sort leads by latest date (newest first)
      const sortedLeads = response.data.sort((a, b) => {
        const dateA = new Date(a.last_seen || a.first_seen || a.event_date || 0);
        const dateB = new Date(b.last_seen || b.first_seen || b.event_date || 0);
        return dateB - dateA;
      });
      
      setLeads(sortedLeads);
      setFilteredLeads(sortedLeads);
    } catch (error) {
      toast.error('Failed to fetch leads');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const handleViewLead = async (lead) => {
    setSelectedLead(lead);
    setViewModalOpen(true);
    setLeadNotes([]);
    
    // Fetch visit history, activity log, and notes
    setLoadingHistory(true);
    setLoadingNotes(true);
    try {
      const [visitsResponse, activityResponse, notesResponse] = await Promise.all([
        axios.get(`${API}/master-visitors/${lead.master_visitor_id}/visits`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/master-visitors/${lead.master_visitor_id}/activity`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/visitors/${lead.master_visitor_id}/notes`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setVisitHistory(visitsResponse.data.visits || []);
      setActivityLog(activityResponse.data || []);
      setLeadNotes(notesResponse.data || []);
    } catch (error) {
      console.error('Failed to fetch lead details:', error);
      toast.error('Failed to load lead details');
      setVisitHistory([]);
      setActivityLog([]);
      setLeadNotes([]);
    } finally {
      setLoadingHistory(false);
      setLoadingNotes(false);
    }
  };

  const handleEditLead = (lead) => {
    setSelectedLead(lead);
    setEditForm({
      name: lead.name || '',
      email: lead.email || '',
      phone: lead.phone || '',
      working_with_agent: lead.working_with_agent || '',
      agent_name: lead.agent_name || '',
      buying_timeline: lead.buying_timeline || '',
      budget_range: lead.budget_range || '',
      preferred_locations: lead.preferred_locations || '',
      interested_in_property: lead.interested_in_property || '',
      request_private_showing: lead.request_private_showing || false,
      notes: lead.notes || '',
      interest_level: lead.interest_level || '',
      tag: lead.tag || ''
    });
    setEditModalOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editForm.name || !editForm.email) {
      toast.error('Name and email are required');
      return;
    }

    setSaving(true);
    try {
      await axios.put(
        `${API}/visitors/${selectedLead.master_visitor_id}`,
        editForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Lead updated successfully');
      setEditModalOpen(false);
      fetchLeads(); // Refresh the list
    } catch (error) {
      console.error('Failed to update lead:', error);
      toast.error(error.response?.data?.detail || 'Failed to update lead');
    } finally {
      setSaving(false);
    }
  };
  // Notes functions
  const fetchLeadNotes = async (visitorId) => {
    setLoadingNotes(true);
    try {
      const res = await axios.get(`${API}/visitors/${visitorId}/notes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeadNotes(res.data);
    } catch (err) {
      console.error('Failed to fetch notes');
    } finally {
      setLoadingNotes(false);
    }
  };

  const handleOpenNotes = (lead) => {
    setNotesLead(lead);
    setNewNoteText('');
    setNotesModalOpen(true);
    fetchLeadNotes(lead.master_visitor_id);
  };

  const handleSaveNote = async () => {
    const lead = notesLead || selectedLead;
    if (!newNoteText.trim() || !lead) return;
    setSavingNote(true);
    try {
      await axios.post(`${API}/visitors/${lead.master_visitor_id}/notes`, {
        text: newNoteText
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Note added');
      setNewNoteText('');
      fetchLeadNotes(lead.master_visitor_id);
    } catch (err) {
      toast.error('Failed to add note');
    } finally {
      setSavingNote(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    const lead = notesLead || selectedLead;
    if (!lead) return;
    try {
      await axios.delete(`${API}/visitors/${lead.master_visitor_id}/notes/${noteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Note deleted');
      fetchLeadNotes(lead.master_visitor_id);
    } catch (err) {
      toast.error('Failed to delete note');
    }
  };

  const handleAddLeadClick = () => {
    setAddLeadForm({
      name: '',
      email: '',
      phone: '',
      working_with_agent: '',
      agent_name: '',
      buying_timeline: '',
      budget_range: '',
      preferred_locations: '',
      interested_in_property: '',
      property_id: '',
      property_address_manual: '',
      contact_date: new Date().toISOString().split('T')[0],
      notes: '',
      source: 'manual'
    });
    setAddLeadModalOpen(true);
  };

  const handleAddLeadSubmit = async () => {
    // Validation
    if (!addLeadForm.name || !addLeadForm.email) {
      toast.error('Name and email are required');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(addLeadForm.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setSaving(true);
    try {
      await axios.post(
        `${API}/leads/manual`,
        addLeadForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Lead added successfully!');
      setAddLeadModalOpen(false);
      fetchLeads();
    } catch (error) {
      console.error('Failed to add lead:', error);
      toast.error(error.response?.data?.detail || 'Failed to add lead');
    } finally {
      setSaving(false);
    }
  };


  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 mb-2">
            <div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#1A4D2E] mb-2" style={{fontFamily: '"Playfair Display", serif'}}>Leads</h1>
              <p className="text-stone-600">All visitors across your open house events</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => { fetchLeads(); toast.success('List refreshed'); }}
                className="p-2.5 rounded-full bg-stone-100 text-stone-600 hover:bg-stone-200 transition-all"
                data-testid="refresh-leads-btn"
                title="Refresh list"
              >
                <RefreshCw size={16} />
              </button>
              <Button
                onClick={async () => {
                  try {
                    const response = await axios.get(`${API}/leads/export-excel`, {
                      headers: { Authorization: `Bearer ${token}` },
                      responseType: 'blob'
                    });
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', `Leads_${new Date().toISOString().split('T')[0]}.xlsx`);
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(url);
                    toast.success('Leads exported successfully!');
                  } catch (err) {
                    toast.error('Failed to export leads');
                  }
                }}
                variant="outline"
                className="border-[#1A4D2E] text-[#1A4D2E] hover:bg-[#1A4D2E]/5"
                data-testid="export-leads-btn"
              >
                <Download size={18} className="mr-2" />
                Export Excel
              </Button>
              <Button
                onClick={handleAddLeadClick}
                className="bg-[#1A4D2E] hover:bg-[#143D24] text-white"
              >
                <Plus size={20} className="mr-2" />
                Add Lead
              </Button>
            </div>
          </div>
        </div>

        {/* Search */}
        <Card className="bg-white p-6 rounded-2xl card-shadow mb-6">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" size={20} />
            <Input
              data-testid="leads-search-input"
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="h-14 bg-stone-50 border-2 border-stone-200 focus:border-[#1A4D2E] focus:ring-0 rounded-lg text-lg pl-12 pr-4"
              placeholder="Search by name, email, or property..."
            />
          </div>
        </Card>

        {/* Leads List */}
        <Card className="bg-white p-8 rounded-2xl card-shadow">
          {loading ? (
            <p className="text-stone-500">Loading leads...</p>
          ) : filteredLeads.length === 0 ? (
            <div className="text-center py-12" data-testid="no-leads-message">
              <Users className="mx-auto mb-4 text-stone-300" size={48} strokeWidth={1.5} />
              <p className="text-lg text-stone-600">
                {searchTerm ? 'No leads found matching your search' : 'No leads yet'}
              </p>
              <p className="text-sm text-stone-500 mt-2">
                {!searchTerm && 'Visitors will appear here as they sign in to your events'}
              </p>
            </div>
          ) : (() => {
            const totalPages = Math.max(1, Math.ceil(filteredLeads.length / leadPageSize));
            const paginated = filteredLeads.slice((leadPage - 1) * leadPageSize, leadPage * leadPageSize);
            return (
              <>
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm text-stone-500">
                    Showing {Math.min((leadPage - 1) * leadPageSize + 1, filteredLeads.length)}-{Math.min(leadPage * leadPageSize, filteredLeads.length)} of {filteredLeads.length} leads
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs text-stone-500">Per page:</span>
                      <Select value={String(leadPageSize)} onValueChange={(v) => { setLeadPageSize(Number(v)); setLeadPage(1); }}>
                        <SelectTrigger className="w-[70px] h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="20">20</SelectItem>
                          <SelectItem value="30">30</SelectItem>
                          <SelectItem value="50">50</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="outline" size="sm" className="h-8 w-8 p-0" disabled={leadPage <= 1} onClick={() => setLeadPage(p => p - 1)}>
                        <ChevronLeft size={16} />
                      </Button>
                      <span className="text-xs font-medium text-stone-700 px-2">{leadPage}/{totalPages}</span>
                      <Button variant="outline" size="sm" className="h-8 w-8 p-0" disabled={leadPage >= totalPages} onClick={() => setLeadPage(p => p + 1)}>
                        <ChevronRight size={16} />
                      </Button>
                    </div>
                  </div>
                </div>
                <div className="space-y-4" data-testid="leads-list">
              {paginated.map((lead) => (
                <Card key={lead.id} className="p-4 md:p-6 rounded-xl border border-stone-200 hover:border-[#1A4D2E] hover:shadow-md transition-all" data-testid={`lead-card-${lead.id}`}>
                  {/* Mobile Layout */}
                  <div className="md:hidden space-y-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-lg font-bold text-[#1A4D2E]" style={{fontFamily: '"Playfair Display", serif'}}>{lead.name}</p>
                        {lead.total_visits > 1 && (
                          <div className="flex items-center gap-1 text-xs font-semibold text-emerald-600 mt-1">
                            <TrendingUp size={12} />
                            <span>{lead.total_visits} visits</span>
                          </div>
                        )}
                        {isBroker && lead.agent_name && (
                          <div className="flex items-center gap-1 text-xs text-stone-500 mt-1">
                            <UserCircle size={12} />
                            <span>{lead.agent_name}</span>
                          </div>
                        )}
                      </div>
                      <span className="text-xs text-stone-500">{formatDate(lead.last_seen || lead.event_date)}</span>
                    </div>
                    
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2 text-stone-600">
                        <Mail size={14} />
                        <span className="truncate">{lead.email}</span>
                      </div>
                      {lead.phone && (
                        <div className="flex items-center gap-2 text-stone-600">
                          <Phone size={14} />
                          <span>{formatPhoneDisplay(lead.phone)}</span>
                        </div>
                      )}
                      <div className="flex items-start gap-2 text-stone-600">
                        <MapPin size={14} className="mt-0.5 flex-shrink-0" />
                        <span className="text-xs">{lead.property_address || '-'}</span>
                      </div>
                    </div>
                    
                    {lead.notes && (
                      <div className="pt-3 border-t border-stone-100">
                        <p className="text-xs text-stone-500 font-semibold mb-1">Notes</p>
                        <p className="text-sm text-stone-600 italic">{lead.notes}</p>
                      </div>
                    )}
                    
                    {/* Action Buttons */}
                    <div className="flex gap-1.5 mt-4 pt-3 border-t border-stone-100">
                      <Button
                        onClick={() => handleViewLead(lead)}
                        variant="outline"
                        size="sm"
                        className="flex-1 border-[#1A4D2E] text-[#1A4D2E] hover:bg-[#1A4D2E] hover:text-white px-2 text-xs h-8"
                      >
                        <Eye size={14} className="mr-1" />
                        View
                      </Button>
                      <Button
                        onClick={() => handleEditLead(lead)}
                        size="sm"
                        className="flex-1 bg-[#1A4D2E] hover:bg-[#143D24] text-white px-2 text-xs h-8"
                      >
                        <Edit size={14} className="mr-1" />
                        Edit
                      </Button>
                      <Button
                        onClick={() => handleOpenNotes(lead)}
                        variant="outline"
                        size="sm"
                        className="flex-1 border-[#1A4D2E] text-[#1A4D2E] hover:bg-[#1A4D2E] hover:text-white px-2 text-xs h-8"
                        data-testid={`notes-btn-mobile-${lead.master_visitor_id}`}
                      >
                        <StickyNote size={14} className="mr-1" />
                        Notes
                      </Button>
                    </div>
                  </div>

                  {/* Desktop Layout */}
                  <div className="hidden md:block">
                    <div className={`grid grid-cols-1 ${isBroker ? 'md:grid-cols-12' : 'md:grid-cols-12'} gap-4`}>
                      <div className="md:col-span-3">
                        <p className="text-sm font-semibold uppercase tracking-wider text-stone-500 mb-1">Name</p>
                        <p className="text-lg font-bold text-[#1A4D2E]" style={{fontFamily: '"Playfair Display", serif'}}>{lead.name}</p>
                        {lead.total_visits > 1 && (
                          <div className="flex items-center gap-1 text-xs font-semibold text-emerald-600 mt-1">
                            <TrendingUp size={14} />
                            <span>{lead.total_visits} visits</span>
                          </div>
                        )}
                      </div>
                      
                      <div className={isBroker ? "md:col-span-3" : "md:col-span-3"}>
                        <p className="text-sm font-semibold uppercase tracking-wider text-stone-500 mb-1">Contact</p>
                        <div className="space-y-1">
                          <div className="flex items-center gap-2 text-stone-600 min-w-0">
                            <Mail size={14} strokeWidth={1.5} className="flex-shrink-0" />
                            <span className="text-sm truncate">{lead.email}</span>
                          </div>
                          {lead.phone && (
                            <div className="flex items-center gap-2 text-stone-600">
                              <Phone size={14} strokeWidth={1.5} className="flex-shrink-0" />
                              <span className="text-sm">{formatPhoneDisplay(lead.phone)}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {isBroker && (
                        <div className="md:col-span-2">
                          <p className="text-sm font-semibold uppercase tracking-wider text-stone-500 mb-1">Agent</p>
                          <div className="flex items-center gap-2 text-stone-600">
                            <UserCircle size={14} strokeWidth={1.5} />
                            <span className="text-sm">{lead.agent_name || (!lead.event_id ? 'Self' : '-')}</span>
                          </div>
                        </div>
                      )}
                      
                      <div className={isBroker ? "md:col-span-2" : "md:col-span-4"}>
                        <p className="text-sm font-semibold uppercase tracking-wider text-stone-500 mb-1">Property</p>
                        <div className="flex items-start gap-2 text-stone-600">
                          <MapPin size={16} strokeWidth={1.5} className="mt-0.5 flex-shrink-0" />
                          <span className="text-sm">{lead.property_address || '-'}</span>
                        </div>
                      </div>
                      
                      <div className="md:col-span-2">
                        <p className="text-sm font-semibold uppercase tracking-wider text-stone-500 mb-1">Last Visit</p>
                        <div className="flex items-center gap-2 text-stone-600">
                          <Calendar size={14} strokeWidth={1.5} />
                          <span className="text-sm">{formatDate(lead.last_seen || lead.event_date)}</span>
                        </div>
                      </div>
                    </div>
                    
                    {lead.notes && (
                      <div className="mt-4 pt-4 border-t border-stone-100">
                        <p className="text-sm font-semibold uppercase tracking-wider text-stone-500 mb-1">Notes</p>
                        <p className="text-sm text-stone-600 italic">{lead.notes}</p>
                      </div>
                    )}
                    
                    {/* Action Buttons */}
                    <div className="flex gap-3 mt-4 pt-4 border-t border-stone-100">
                      {(!isBroker || !lead.agent_name) && (
                        <>
                          <Button
                            onClick={() => handleViewLead(lead)}
                            variant="outline"
                            className="border-[#1A4D2E] text-[#1A4D2E] hover:bg-[#1A4D2E] hover:text-white"
                          >
                            <Eye size={18} className="mr-2" />
                            View Details
                          </Button>
                          <Button
                            onClick={() => handleEditLead(lead)}
                            className="bg-[#1A4D2E] hover:bg-[#143D24] text-white"
                          >
                            <Edit size={18} className="mr-2" />
                            Edit Lead
                          </Button>
                          <Button
                            onClick={() => handleOpenNotes(lead)}
                            variant="outline"
                            className="border-[#1A4D2E] text-[#1A4D2E] hover:bg-[#1A4D2E] hover:text-white"
                            data-testid={`notes-btn-${lead.master_visitor_id}`}
                          >
                            <StickyNote size={18} className="mr-2" />
                            Notes
                          </Button>
                        </>
                      )}
                      {!lead.event_id && (
                        <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold bg-purple-100 text-purple-700 border border-purple-200" data-testid="private-lead-badge">
                          Private Lead
                        </span>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
              </>
            );
          })()}
        </Card>

        {/* View Lead Modal with Visit History and Activity */}
        <Dialog open={viewModalOpen} onOpenChange={setViewModalOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-[#1A4D2E]">Lead Details</DialogTitle>
            </DialogHeader>
            {selectedLead && (
              <Tabs defaultValue="details" className="mt-4">
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="details" className="text-xs px-1">
                    <UserCircle size={14} className="mr-1 hidden sm:inline" />
                    Details
                  </TabsTrigger>
                  <TabsTrigger value="notes" className="text-xs px-1" onClick={() => fetchLeadNotes(selectedLead.master_visitor_id)}>
                    <StickyNote size={14} className="mr-1 hidden sm:inline" />
                    Notes
                  </TabsTrigger>
                  <TabsTrigger value="visits" className="text-xs px-1">
                    <History size={14} className="mr-1 hidden sm:inline" />
                    Visits
                  </TabsTrigger>
                  <TabsTrigger value="activity" className="text-xs px-1">
                    <Activity size={14} className="mr-1 hidden sm:inline" />
                    Activity
                  </TabsTrigger>
                </TabsList>

                {/* Details Tab */}
                <TabsContent value="details" className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-stone-600 text-sm">Full Name</Label>
                      <p className="text-lg font-semibold text-stone-900">{selectedLead.name}</p>
                    </div>
                    <div>
                      <Label className="text-stone-600 text-sm">Total Visits</Label>
                      <p className="text-lg font-semibold text-emerald-600">{selectedLead.total_visits || 1}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-stone-600 text-sm flex items-center gap-2">
                        <Mail size={14} />
                        Email
                      </Label>
                      <p className="text-stone-900">{selectedLead.email}</p>
                    </div>
                    <div>
                      <Label className="text-stone-600 text-sm flex items-center gap-2">
                        <Phone size={14} />
                        Phone
                      </Label>
                      <p className="text-stone-900">{formatPhoneDisplay(selectedLead.phone)}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-stone-600 text-sm">First Seen</Label>
                      <p className="text-stone-900">{formatDate(selectedLead.first_seen)}</p>
                    </div>
                    <div>
                      <Label className="text-stone-600 text-sm">Last Seen</Label>
                      <p className="text-stone-900">{formatDate(selectedLead.last_seen)}</p>
                    </div>
                  </div>

                  <div>
                    <Label className="text-stone-600 text-sm flex items-center gap-2">
                      <MapPin size={14} />
                      Most Recent Property
                    </Label>
                    <p className="text-stone-900">{selectedLead.property_address || 'Not provided'}</p>
                  </div>

                  {isBroker && selectedLead.agent_name && (
                    <div>
                      <Label className="text-stone-600 text-sm flex items-center gap-2">
                        <UserCircle size={14} />
                        Agent
                      </Label>
                      <p className="text-stone-900">{selectedLead.agent_name}</p>
                    </div>
                  )}

                  {selectedLead.properties_visited && selectedLead.properties_visited.length > 1 && (
                    <div>
                      <Label className="text-stone-600 text-sm">Properties Visited</Label>
                      <ul className="mt-2 space-y-1">
                        {selectedLead.properties_visited.map((address, idx) => (
                          <li key={idx} className="text-sm text-stone-700 flex items-start gap-2">
                            <MapPin size={14} className="mt-0.5 flex-shrink-0 text-stone-400" />
                            <span>{address}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {selectedLead.interest_level && (
                    <div>
                      <Label className="text-stone-600 text-sm">Interest Level</Label>
                      <p className="text-stone-900 bg-stone-50 p-3 rounded capitalize">{selectedLead.interest_level}</p>
                    </div>
                  )}

                  {selectedLead.tag && (
                    <div>
                      <Label className="text-stone-600 text-sm">Tag</Label>
                      <p className="text-stone-900 bg-stone-50 p-3 rounded capitalize">{selectedLead.tag}</p>
                    </div>
                  )}

                  {selectedLead.notes && (
                    <div>
                      <Label className="text-stone-600 text-sm">Notes</Label>
                      <p className="text-stone-900 italic bg-stone-50 p-3 rounded">{selectedLead.notes}</p>
                    </div>
                  )}
                </TabsContent>

                {/* Notes Tab */}
                <TabsContent value="notes" className="mt-4" data-testid="lead-notes-tab">
                  {/* Add Note */}
                  <div className="flex gap-2 mb-4">
                    <Textarea
                      value={newNoteText}
                      onChange={(e) => setNewNoteText(e.target.value)}
                      placeholder="Add a note..."
                      rows={2}
                      className="resize-none text-sm flex-1"
                      data-testid="lead-note-input"
                    />
                    <Button
                      onClick={handleSaveNote}
                      disabled={savingNote || !newNoteText.trim()}
                      className="bg-[#1A4D2E] hover:bg-[#143D24] text-white self-end"
                      data-testid="save-note-btn"
                    >
                      {savingNote ? <Loader2 size={16} className="animate-spin" /> : 'Add'}
                    </Button>
                  </div>

                  {/* Notes List */}
                  {loadingNotes ? (
                    <div className="text-center py-8 text-stone-500">Loading notes...</div>
                  ) : leadNotes.length === 0 ? (
                    <div className="text-center py-8 text-stone-500">No notes yet. Add one above.</div>
                  ) : (
                    <div className="space-y-3 max-h-[400px] overflow-y-auto">
                      {leadNotes.map((note) => (
                        <div key={note.id} className="p-3 bg-stone-50 rounded-lg border border-stone-200 group" data-testid={`note-item-${note.id}`}>
                          <div className="flex items-start justify-between">
                            <p className="text-sm text-stone-800 whitespace-pre-wrap flex-1">{note.text}</p>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteNote(note.id)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50 h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 ml-2"
                              data-testid={`delete-note-${note.id}`}
                            >
                              <Trash2 size={14} />
                            </Button>
                          </div>
                          <div className="flex items-center gap-2 mt-2 text-xs text-stone-400">
                            <span>{note.created_by || note.agent_name || 'Unknown'}</span>
                            <span>&middot;</span>
                            <span>{formatDate(note.created_at)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>

                {/* Visit History Tab */}
                <TabsContent value="visits" className="mt-4">
                  {loadingHistory ? (
                    <div className="text-center py-8 text-stone-500">Loading visit history...</div>
                  ) : visitHistory.length === 0 ? (
                    <div className="text-center py-8 text-stone-500">No visit history available</div>
                  ) : (
                    <div className="space-y-3 max-h-[500px] overflow-y-auto">
                      {visitHistory.map((visit, idx) => (
                        <Card key={visit.visit_id} className="p-4 border border-stone-200">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <p className="font-semibold text-stone-900">Visit #{visitHistory.length - idx}</p>
                              <p className="text-sm text-stone-500">{formatDate(visit.visit_date)}</p>
                            </div>
                            {visit.lead_status && (
                              <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                visit.lead_status === 'hot' ? 'bg-red-100 text-red-700' :
                                visit.lead_status === 'warm' ? 'bg-yellow-100 text-yellow-700' :
                                visit.lead_status === 'cold' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                              }`}>
                                {visit.lead_status}
                              </span>
                            )}
                          </div>
                          
                          <div className="space-y-2 text-sm">
                            {visit.property_address && (
                              <div className="flex items-start gap-2">
                                <MapPin size={14} className="mt-0.5 text-stone-400 flex-shrink-0" />
                                <span className="text-stone-700">{visit.property_address}</span>
                              </div>
                            )}
                            {visit.agent_name && (
                              <div className="flex items-center gap-2">
                                <UserCircle size={14} className="text-stone-400" />
                                <span className="text-stone-700">{visit.agent_name}</span>
                              </div>
                            )}
                            {visit.buying_timeline && (
                              <p className="text-stone-600">
                                <span className="font-medium">Timeline:</span> {visit.buying_timeline}
                              </p>
                            )}
                            {visit.budget_range && (
                              <p className="text-stone-600">
                                <span className="font-medium">Budget:</span> {visit.budget_range}
                              </p>
                            )}
                            {visit.interested_in_property && (
                              <p className="text-stone-600">
                                <span className="font-medium">Interest:</span> {visit.interested_in_property}
                              </p>
                            )}
                            {visit.request_private_showing && (
                              <p className="text-emerald-600 font-medium">
                                ✓ Requested private showing
                              </p>
                            )}
                            {visit.notes && (
                              <p className="text-stone-600 italic bg-stone-50 p-2 rounded mt-2">
                                {visit.notes}
                              </p>
                            )}
                          </div>
                        </Card>
                      ))}
                    </div>
                  )}
                </TabsContent>

                {/* Activity Log Tab */}
                <TabsContent value="activity" className="mt-4">
                  {loadingHistory ? (
                    <div className="text-center py-8 text-stone-500">Loading activity log...</div>
                  ) : activityLog.length === 0 ? (
                    <div className="text-center py-8 text-stone-500">No activity recorded yet</div>
                  ) : (
                    <div className="space-y-2 max-h-[500px] overflow-y-auto">
                      {activityLog.map((activity) => (
                        <div key={activity.activity_id} className="flex gap-3 py-3 border-b border-stone-100 last:border-0">
                          <div className="flex-shrink-0 w-2 h-2 rounded-full bg-[#1A4D2E] mt-2"></div>
                          <div className="flex-1">
                            <p className="text-sm text-stone-900">{activity.description}</p>
                            <p className="text-xs text-stone-500 mt-1">
                              {new Date(activity.created_at).toLocaleString()} • {activity.created_by}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit Lead Modal */}
        <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-[#1A4D2E]">Edit Lead</DialogTitle>
            </DialogHeader>
            {selectedLead && (
              <div className="space-y-5 mt-4">
                {/* Contact Info */}
                <div>
                  <p className="text-sm font-semibold text-stone-500 uppercase tracking-wider mb-3">Contact Information</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="edit-name">Full Name *</Label>
                      <Input
                        id="edit-name"
                        data-testid="edit-lead-name"
                        value={editForm.name}
                        onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                        className="mt-1 bg-white"
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit-email">Email *</Label>
                      <Input
                        id="edit-email"
                        data-testid="edit-lead-email"
                        type="email"
                        value={editForm.email}
                        onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                        className="mt-1 bg-white"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <Label htmlFor="edit-phone">Phone</Label>
                      <Input
                        id="edit-phone"
                        data-testid="edit-lead-phone"
                        value={editForm.phone}
                        onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                        className="mt-1 bg-white"
                      />
                    </div>
                  </div>
                </div>

                {/* Sign-In Sheet Fields */}
                <div>
                  <p className="text-sm font-semibold text-stone-500 uppercase tracking-wider mb-3">Sign-In Details</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="edit-working-with-agent">Working with Agent?</Label>
                      <select
                        id="edit-working-with-agent"
                        data-testid="edit-lead-working-with-agent"
                        value={editForm.working_with_agent}
                        onChange={(e) => setEditForm({...editForm, working_with_agent: e.target.value})}
                        className="w-full mt-1 h-10 px-3 rounded-md border border-stone-300 bg-white focus:border-[#1A4D2E] focus:ring-2 focus:ring-[#1A4D2E]/20"
                      >
                        <option value="">Select</option>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="edit-agent-name">Agent Name</Label>
                      <Input
                        id="edit-agent-name"
                        data-testid="edit-lead-agent-name"
                        value={editForm.agent_name}
                        onChange={(e) => setEditForm({...editForm, agent_name: e.target.value})}
                        className="mt-1 bg-white"
                        placeholder="If working with an agent"
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit-buying-timeline">Buying Timeline</Label>
                      <select
                        id="edit-buying-timeline"
                        data-testid="edit-lead-buying-timeline"
                        value={editForm.buying_timeline}
                        onChange={(e) => setEditForm({...editForm, buying_timeline: e.target.value})}
                        className="w-full mt-1 h-10 px-3 rounded-md border border-stone-300 bg-white focus:border-[#1A4D2E] focus:ring-2 focus:ring-[#1A4D2E]/20"
                      >
                        <option value="">Select</option>
                        <option value="immediately">Immediately</option>
                        <option value="1-3 months">1-3 Months</option>
                        <option value="3-6 months">3-6 Months</option>
                        <option value="6-12 months">6-12 Months</option>
                        <option value="just looking">Just Looking</option>
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="edit-budget-range">Budget Range</Label>
                      <select
                        id="edit-budget-range"
                        data-testid="edit-lead-budget-range"
                        value={editForm.budget_range}
                        onChange={(e) => setEditForm({...editForm, budget_range: e.target.value})}
                        className="w-full mt-1 h-10 px-3 rounded-md border border-stone-300 bg-white focus:border-[#1A4D2E] focus:ring-2 focus:ring-[#1A4D2E]/20"
                      >
                        <option value="">Select</option>
                        <option value="under 250k">Under $250K</option>
                        <option value="250k-500k">$250K - $500K</option>
                        <option value="500k-750k">$500K - $750K</option>
                        <option value="750k-1m">$750K - $1M</option>
                        <option value="1m-2m">$1M - $2M</option>
                        <option value="2m-5m">$2M - $5M</option>
                        <option value="5m+">$5M+</option>
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="edit-preferred-locations">Preferred Locations</Label>
                      <Input
                        id="edit-preferred-locations"
                        data-testid="edit-lead-preferred-locations"
                        value={editForm.preferred_locations}
                        onChange={(e) => setEditForm({...editForm, preferred_locations: e.target.value})}
                        className="mt-1 bg-white"
                        placeholder="e.g. Montclair, Edison"
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit-interested-in-property">Interested in Property?</Label>
                      <select
                        id="edit-interested-in-property"
                        data-testid="edit-lead-interested-in-property"
                        value={editForm.interested_in_property}
                        onChange={(e) => setEditForm({...editForm, interested_in_property: e.target.value})}
                        className="w-full mt-1 h-10 px-3 rounded-md border border-stone-300 bg-white focus:border-[#1A4D2E] focus:ring-2 focus:ring-[#1A4D2E]/20"
                      >
                        <option value="">Select</option>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                        <option value="maybe">Maybe</option>
                      </select>
                    </div>
                    <div className="sm:col-span-2 flex items-center gap-2 mt-1">
                      <input
                        type="checkbox"
                        id="edit-request-private-showing"
                        data-testid="edit-lead-request-private-showing"
                        checked={editForm.request_private_showing}
                        onChange={(e) => setEditForm({...editForm, request_private_showing: e.target.checked})}
                        className="h-4 w-4 rounded border-stone-300 text-[#1A4D2E] focus:ring-[#1A4D2E]"
                      />
                      <Label htmlFor="edit-request-private-showing" className="mb-0 cursor-pointer">Request Private Showing</Label>
                    </div>
                  </div>
                </div>

                {/* Agent Classification */}
                <div>
                  <p className="text-sm font-semibold text-stone-500 uppercase tracking-wider mb-3">Classification</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="edit-interest">Interest Level</Label>
                      <select
                        id="edit-interest"
                        data-testid="edit-lead-interest-level"
                        value={editForm.interest_level}
                        onChange={(e) => setEditForm({...editForm, interest_level: e.target.value})}
                        className="w-full mt-1 h-10 px-3 rounded-md border border-stone-300 bg-white focus:border-[#1A4D2E] focus:ring-2 focus:ring-[#1A4D2E]/20"
                      >
                        <option value="">Select level</option>
                        <option value="high">High</option>
                        <option value="medium">Medium</option>
                        <option value="low">Low</option>
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="edit-tag">Tag</Label>
                      <select
                        id="edit-tag"
                        data-testid="edit-lead-tag"
                        value={editForm.tag}
                        onChange={(e) => setEditForm({...editForm, tag: e.target.value})}
                        className="w-full mt-1 h-10 px-3 rounded-md border border-stone-300 bg-white focus:border-[#1A4D2E] focus:ring-2 focus:ring-[#1A4D2E]/20"
                      >
                        <option value="">No tag</option>
                        <option value="hot">Hot</option>
                        <option value="warm">Warm</option>
                        <option value="cold">Cold</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div>
                  <Label htmlFor="edit-notes">Notes</Label>
                  <Textarea
                    id="edit-notes"
                    data-testid="edit-lead-notes"
                    value={editForm.notes}
                    onChange={(e) => setEditForm({...editForm, notes: e.target.value})}
                    rows={3}
                    className="mt-1 bg-white"
                    placeholder="Add any notes about this lead..."
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    onClick={handleSaveEdit}
                    disabled={saving}
                    data-testid="edit-lead-save-btn"
                    className="flex-1 bg-[#1A4D2E] hover:bg-[#143D24] text-white"
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                  <Button
                    onClick={() => setEditModalOpen(false)}
                    variant="outline"
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

      {/* Add Lead Modal */}
      <Dialog open={addLeadModalOpen} onOpenChange={setAddLeadModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#1A4D2E]">Add New Lead</DialogTitle>
            <p className="text-sm text-stone-600">Enter lead information manually</p>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Basic Information */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-semibold text-stone-700 mb-2">Full Name *</Label>
                <Input
                  value={addLeadForm.name}
                  onChange={(e) => setAddLeadForm({...addLeadForm, name: e.target.value})}
                  placeholder="John Doe"
                />
              </div>

              <div>
                <Label className="text-sm font-semibold text-stone-700 mb-2">Email *</Label>
                <Input
                  type="email"
                  value={addLeadForm.email}
                  onChange={(e) => setAddLeadForm({...addLeadForm, email: e.target.value})}
                  placeholder="john@example.com"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-semibold text-stone-700 mb-2">Phone Number</Label>
                <PhoneInput
                  value={addLeadForm.phone}
                  onChange={(value) => {
                    // Handle both string and object from PhoneInput
                    const phoneValue = typeof value === 'string' ? value : value?.target?.value || '';
                    setAddLeadForm({...addLeadForm, phone: phoneValue});
                  }}
                />
              </div>

              <div>
                <Label className="text-sm font-semibold text-stone-700 mb-2">Contact Date *</Label>
                <Input
                  type="date"
                  value={addLeadForm.contact_date}
                  onChange={(e) => setAddLeadForm({...addLeadForm, contact_date: e.target.value})}
                />
              </div>
            </div>

            {/* Property Selection */}
            <div>
              <Label className="text-sm font-semibold text-stone-700 mb-2">Associated Open House Property</Label>
              <select
                value={addLeadForm.property_id}
                onChange={(e) => setAddLeadForm({...addLeadForm, property_id: e.target.value, property_address_manual: ''})}
                className="w-full h-10 px-3 rounded-md border border-stone-300 focus:border-[#1A4D2E] focus:ring-1 focus:ring-[#1A4D2E]"
              >
                <option value="">Select Open House Property (Optional)</option>
                {openHouses.map(event => (
                  <option key={event.id} value={event.id}>
                    {event.property_address} - {(() => { const [y,m,d] = event.date.split('-'); return new Date(y, m-1, d).toLocaleDateString(); })()}
                  </option>
                ))}
              </select>
            </div>

            {/* Manual Property Address */}
            <div>
              <Label className="text-sm font-semibold text-stone-700 mb-2">Or Enter Property Address Manually</Label>
              <Input
                value={addLeadForm.property_address_manual}
                onChange={(e) => setAddLeadForm({...addLeadForm, property_address_manual: e.target.value, property_id: ''})}
                placeholder="e.g., 123 Main Street, Miami, FL 33101"
                disabled={!!addLeadForm.property_id}
              />
              <p className="text-xs text-stone-500 mt-1">
                {addLeadForm.property_id ? 'Clear dropdown selection to enter manually' : 'Enter address if property is not in the list'}
              </p>
            </div>

            {/* Buyer Information */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-semibold text-stone-700 mb-2">Working with an agent?</Label>
                <select
                  value={addLeadForm.working_with_agent}
                  onChange={(e) => setAddLeadForm({...addLeadForm, working_with_agent: e.target.value})}
                  className="w-full h-10 px-3 rounded-md border border-stone-300 focus:border-[#1A4D2E] focus:ring-1 focus:ring-[#1A4D2E]"
                >
                  <option value="">Select</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>

              {addLeadForm.working_with_agent === 'yes' && (
                <div>
                  <Label className="text-sm font-semibold text-stone-700 mb-2">Agent Name</Label>
                  <Input
                    value={addLeadForm.agent_name}
                    onChange={(e) => setAddLeadForm({...addLeadForm, agent_name: e.target.value})}
                    placeholder="Enter agent's name"
                  />
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-semibold text-stone-700 mb-2">Buying Timeline</Label>
                <select
                  value={addLeadForm.buying_timeline}
                  onChange={(e) => setAddLeadForm({...addLeadForm, buying_timeline: e.target.value})}
                  className="w-full h-10 px-3 rounded-md border border-stone-300 focus:border-[#1A4D2E] focus:ring-1 focus:ring-[#1A4D2E]"
                >
                  <option value="">Select</option>
                  <option value="immediately">Immediately (0-3 months)</option>
                  <option value="soon">Soon (3-6 months)</option>
                  <option value="later">Later this year (6-12 months)</option>
                  <option value="exploring">Just exploring</option>
                </select>
              </div>

              <div>
                <Label className="text-sm font-semibold text-stone-700 mb-2">Budget Range</Label>
                <select
                  value={addLeadForm.budget_range}
                  onChange={(e) => setAddLeadForm({...addLeadForm, budget_range: e.target.value})}
                  className="w-full h-10 px-3 rounded-md border border-stone-300 focus:border-[#1A4D2E] focus:ring-1 focus:ring-[#1A4D2E]"
                >
                  <option value="">Select</option>
                  <option value="under-300k">Under $300k</option>
                  <option value="300k-500k">$300k - $500k</option>
                  <option value="500k-750k">$500k - $750k</option>
                  <option value="750k-1m">$750k - $1M</option>
                  <option value="1m-2m">$1M - $2M</option>
                  <option value="over-2m">Over $2M</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-semibold text-stone-700 mb-2">Preferred Locations</Label>
                <Input
                  value={addLeadForm.preferred_locations}
                  onChange={(e) => setAddLeadForm({...addLeadForm, preferred_locations: e.target.value})}
                  placeholder="e.g., Miami Beach, Coral Gables"
                />
              </div>

              <div>
                <Label className="text-sm font-semibold text-stone-700 mb-2">Interested in Property?</Label>
                <select
                  value={addLeadForm.interested_in_property}
                  onChange={(e) => setAddLeadForm({...addLeadForm, interested_in_property: e.target.value})}
                  className="w-full h-10 px-3 rounded-md border border-stone-300 focus:border-[#1A4D2E] focus:ring-1 focus:ring-[#1A4D2E]"
                >
                  <option value="">Select</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                  <option value="maybe">Maybe</option>
                </select>
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label className="text-sm font-semibold text-stone-700 mb-2">Notes</Label>
              <Textarea
                value={addLeadForm.notes}
                onChange={(e) => setAddLeadForm({...addLeadForm, notes: e.target.value})}
                placeholder="Add any additional notes about this lead..."
                rows={3}
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                onClick={() => setAddLeadModalOpen(false)}
                variant="outline"
                className="flex-1"
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddLeadSubmit}
                className="flex-1 bg-[#1A4D2E] hover:bg-[#143D24] text-white"
                disabled={saving}
              >
                {saving ? 'Adding...' : 'Add Lead'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Notes Popup Modal */}
      <Dialog open={notesModalOpen} onOpenChange={setNotesModalOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#1A4D2E] flex items-center gap-2">
              <StickyNote size={20} />
              Notes — {notesLead?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="flex gap-2 mt-2">
            <Textarea
              value={newNoteText}
              onChange={(e) => setNewNoteText(e.target.value)}
              placeholder="Add a note..."
              rows={2}
              className="resize-none text-sm flex-1"
              data-testid="notes-popup-input"
            />
            <Button
              onClick={handleSaveNote}
              disabled={savingNote || !newNoteText.trim()}
              className="bg-[#1A4D2E] hover:bg-[#143D24] text-white self-end"
              data-testid="notes-popup-save-btn"
            >
              {savingNote ? <Loader2 size={16} className="animate-spin" /> : 'Add'}
            </Button>
          </div>
          <div className="flex-1 overflow-y-auto mt-3 space-y-2">
            {loadingNotes ? (
              <div className="text-center py-6 text-stone-500">Loading...</div>
            ) : leadNotes.length === 0 ? (
              <div className="text-center py-6 text-stone-500">No notes yet.</div>
            ) : (
              leadNotes.map((note) => (
                <div key={note.id} className="p-3 bg-stone-50 rounded-lg border border-stone-200 group" data-testid={`popup-note-${note.id}`}>
                  <div className="flex items-start justify-between">
                    <p className="text-sm text-stone-800 whitespace-pre-wrap flex-1">{note.text}</p>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteNote(note.id)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50 h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 ml-2"
                      data-testid={`popup-delete-note-${note.id}`}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                  <div className="flex items-center gap-2 mt-2 text-xs text-stone-400">
                    <span>{note.created_by || note.agent_name || 'Unknown'}</span>
                    <span>&middot;</span>
                    <span>{formatDate(note.created_at)}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      </div>
    </Layout>
  );
};

export default Leads;
